/**
 * Wizard card for the ghl_discovery + ghl_config steps (Slice 4).
 *
 * One card covers both coupled steps. It is rendered for the `ghl_discovery`
 * step key (see OnboardingRunPage CARD_COMPONENTS); the `ghl_config` row renders
 * nothing of its own — its status still shows in the checklist, and this card
 * drives both rows via the onboard-gohighlevel edge fn. Because save_config sets
 * the ghl_discovery row to `done` too, the card can derive its whole state from
 * the ghl_discovery `task` prop alone.
 *
 * Two phases, admin-driven, modeled on LimoAnywhereCard:
 *   1. Creds sub-form (API key + Location ID) → `save_and_discover`, which stores
 *      the GHL credentials (plaintext account_settings, Option A) and fetches the
 *      location's pipelines/stages. The task flips to needs_review.
 *   2. Config selects (pipeline → quoted/booked stage, stages scoped to the
 *      chosen pipeline) → `save_config`, which validates + persists the IDs and
 *      flips the task(s) to done.
 *
 * The API key is never echoed back; once stored the field shows "stored ✓" and an
 * empty key on re-submit means "keep existing". Saved settings are read back on
 * mount so the form survives a refresh. Status auto-refreshes via the run page's
 * 5s poll.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface GoHighLevelCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface PipelineStage {
  id: string
  name: string
}

interface Pipeline {
  id: string
  name: string
  stages: PipelineStage[]
}

interface GhlDiscoveryResult {
  pipelines?: Pipeline[]
  locationId?: string
  apiKeyStored?: boolean
  fetchedAt?: string
}

const credsSchema = z.object({
  apiKey: z.string().optional(),
  locationId: z.string().trim().min(1, 'Location ID is required'),
})

type CredsFormData = z.infer<typeof credsSchema>

export function GoHighLevelCard({
  task,
  accountId,
  runId,
}: GoHighLevelCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as GhlDiscoveryResult
  const isDone = task.status === 'done'
  const needsReview = task.status === 'needs_review'

  const pipelines = useMemo(() => result.pipelines ?? [], [result.pipelines])

  const form = useForm<CredsFormData>({
    resolver: zodResolver(credsSchema),
    defaultValues: { apiKey: '', locationId: '' },
  })

  // Read back saved settings so the form + selections survive a page refresh.
  // The API key itself is never read back — only its presence (for "stored ✓").
  const { data: savedSettings } = useQuery({
    queryKey: ['onboarding-ghl-settings', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', [
          'go_high_level_api_key',
          'go_high_level_location_id',
          'go_high_level_pipeline_id',
          'go_high_level_stage_quoted',
          'go_high_level_stage_booked',
        ])
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  // Seed the creds form from saved settings exactly once (a guard prevents a
  // background refetch from clobbering the admin's in-progress edits).
  const seeded = useRef(false)
  useEffect(() => {
    if (!savedSettings || seeded.current) return
    seeded.current = true
    form.reset({
      apiKey: '',
      locationId: savedSettings['go_high_level_location_id'] ?? '',
    })
  }, [savedSettings, form])

  const apiKeyStored =
    Boolean(result.apiKeyStored) ||
    Boolean(savedSettings?.['go_high_level_api_key'])

  // Config selection: DERIVE from saved settings + the admin's overrides rather
  // than seeding via an effect — deriving avoids a setState-in-effect cascade
  // (same pattern as LimoAnywhereCard). An empty-string override is a real value
  // (`'' ?? saved` === ''), so a stage cleared on pipeline change stays cleared.
  const [override, setOverride] = useState<{
    pipelineId?: string
    stageQuoted?: string
    stageBooked?: string
  }>({})

  const selection = {
    pipelineId:
      override.pipelineId ?? savedSettings?.['go_high_level_pipeline_id'] ?? '',
    stageQuoted:
      override.stageQuoted ?? savedSettings?.['go_high_level_stage_quoted'] ?? '',
    stageBooked:
      override.stageBooked ?? savedSettings?.['go_high_level_stage_booked'] ?? '',
  }

  const selectedPipeline = pipelines.find((p) => p.id === selection.pipelineId)
  const stages = selectedPipeline?.stages ?? []

  const locationIdValue = useWatch({ control: form.control, name: 'locationId' })

  const onPipelineChange = (pipelineId: string) => {
    const next = pipelines.find((p) => p.id === pipelineId)
    const stageIds = new Set((next?.stages ?? []).map((s) => s.id))
    // Keep stage selections only if they belong to the newly chosen pipeline.
    setOverride({
      pipelineId,
      stageQuoted: stageIds.has(selection.stageQuoted) ? selection.stageQuoted : '',
      stageBooked: stageIds.has(selection.stageBooked) ? selection.stageBooked : '',
    })
  }

  const saveAndDiscover = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-gohighlevel',
        accountId,
        params: {
          action: 'save_and_discover',
          // Only send a key when the admin typed one (empty = keep stored).
          ...(values.apiKey ? { apiKey: values.apiKey } : {}),
          locationId: values.locationId,
        },
      },
      {
        onSuccess: () => form.setValue('apiKey', ''),
        onError: (error) =>
          toast({
            title: 'GoHighLevel discovery failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  const saveConfig = () => {
    if (!selection.pipelineId || !selection.stageQuoted || !selection.stageBooked) {
      toast({
        title: 'Incomplete configuration',
        description: 'Choose a pipeline plus a Quoted and Booked stage.',
        variant: 'destructive',
      })
      return
    }
    invokeStep.mutate(
      {
        functionName: 'onboard-gohighlevel',
        accountId,
        params: {
          action: 'save_config',
          pipelineId: selection.pipelineId,
          stageQuoted: selection.stageQuoted,
          stageBooked: selection.stageBooked,
        },
      },
      {
        onError: (error) =>
          toast({
            title: 'GoHighLevel config failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  const reconfigure = () => {
    const locationId = savedSettings?.['go_high_level_location_id']
    if (!locationId) return
    setOverride({})
    invokeStep.mutate({
      functionName: 'onboard-gohighlevel',
      accountId,
      params: { action: 'save_and_discover', locationId },
    })
  }

  const donePipelineName =
    pipelines.find((p) => p.id === selection.pipelineId)?.name ?? null

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          If discovery is unavailable, the client can configure GoHighLevel
          directly under Settings &gt; Integrations &gt; GoHighLevel in their
          dashboard (API Key, Location ID, Pipeline, Quoted/Booked stage IDs).
          This step is safe to re-run later.
        </p>
      }
    >
      {isDone ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              GoHighLevel configured
              {donePipelineName ? ` (${donePipelineName})` : ''}.
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={reconfigure}
            disabled={invokeStep.isPending || !savedSettings?.['go_high_level_location_id']}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-fetch / reconfigure
          </Button>
        </div>
      ) : (
        <form onSubmit={saveAndDiscover} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ghl-key">
              API Key
              {apiKeyStored && (
                <span className="ml-2 text-xs text-green-600">stored ✓</span>
              )}
            </Label>
            <Input
              id="ghl-key"
              type="password"
              autoComplete="off"
              placeholder={apiKeyStored ? 'Leave blank to keep stored key' : ''}
              {...form.register('apiKey')}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ghl-location">Location ID</Label>
            <Input id="ghl-location" {...form.register('locationId')} />
            {form.formState.errors.locationId && (
              <p className="text-xs text-red-600">
                {form.formState.errors.locationId.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            size="sm"
            disabled={invokeStep.isPending || !locationIdValue}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : needsReview ? (
              <RefreshCw className="mr-2 h-4 w-4" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            {needsReview ? 'Re-fetch pipelines' : 'Save & discover'}
          </Button>
        </form>
      )}

      {needsReview && pipelines.length > 0 && (
        <div className="mt-4 space-y-3 border-t pt-4">
          <p className="text-sm font-medium">Map the GoHighLevel pipeline</p>

          <div className="space-y-1">
            <Label>Pipeline</Label>
            <Select value={selection.pipelineId} onValueChange={onPipelineChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Quoted stage</Label>
              <Select
                value={selection.stageQuoted}
                onValueChange={(value) =>
                  setOverride((o) => ({ ...o, stageQuoted: value }))
                }
                disabled={!selectedPipeline}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Booked stage</Label>
              <Select
                value={selection.stageBooked}
                onValueChange={(value) =>
                  setOverride((o) => ({ ...o, stageBooked: value }))
                }
                disabled={!selectedPipeline}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button size="sm" onClick={saveConfig} disabled={invokeStep.isPending}>
            {invokeStep.isPending && (
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            )}
            Save configuration
          </Button>
        </div>
      )}
    </StepCard>
  )
}
